import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ProductScanSource,
  ProfileViewSource,
  StatusBoardApi,
  StatusBoardPeriodChangeDirection,
  type FeedbackStatsDto,
  type StatusBoardDto,
  type StatusBoardProductsDto,
  type StatusBoardProfileViewsDto,
} from '@up2stay/up2promo-api-client';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  DASHBOARD_REFRESH_QUERY_KEYS,
  invalidateDashboardStatusQueries,
} from '@/core/components/dashboard/dashboard-refresh-keys';
import { buildDashboardDoNextItems } from '@/core/components/dashboard/build-dashboard-do-next';
import { buildDashboardHealthRows } from '@/core/components/dashboard/build-dashboard-health-rows';
import {
  ATTENTION_NOTIFICATION_LIMIT,
  ATTENTION_REVIEW_LIMIT,
  EMPTY_PRODUCT_METRICS,
  EMPTY_PROFILE_VIEW_METRICS,
  LIVE_SCAN_LIMIT,
  buildScanSeries,
  decorateStatusBoardLinkTargets,
  decorateStatusBoardTopCards,
  healthStatusBucket,
  periodOverPeriodChange,
} from '@/core/components/dashboard/dashboard-status-helpers';
import type {
  DashboardHealthMix,
  DashboardLiveScan,
  DashboardPeriodChange,
  DashboardProductMetrics,
  DashboardProfileViewMetrics,
  DashboardRangeDays,
  DashboardStatusLabels,
} from '@/core/components/dashboard/dashboard-status-types';
import { useInvalidateOnIonViewEnter } from '@/core/hooks/use-invalidate-on-ion-view-enter';
import {
  useNotificationInbox,
  useUnreadNotificationCount,
} from '@/core/hooks/use-notifications';
import { useReputationConnectionSetupState } from '@/core/hooks/use-reputation-connection-setup';
import {
  useWorkspaceRealtime,
  type WorkspaceRealtimeConnectionState,
} from '@/core/hooks/use-workspace-realtime';
import { getAbsoluteFileUrl } from '@/core/lib/file-url';
import { locationIdsWithFailedSync } from '@/core/lib/channel-location-sync';
import { statusBoardQueryKeys } from '@/core/query-keys/status-board-query-keys';
import { useCurrentWorkspace } from '@/core/store/workspace.store';
import {
  type RealtimeEvent,
  toProductScan,
} from '@/core/types/realtime-events';
import { isAuthzApiError } from '@/core/utils/api-error.utils';

export type {
  DashboardDoNextItem,
  DashboardHealthMix,
  DashboardHealthRow,
  DashboardHealthStatus,
  DashboardLiveScan,
  DashboardProductMetrics,
  DashboardProfileViewMetrics,
  DashboardRangeDays,
  DashboardScanPoint,
  DashboardStatusLabels,
} from './dashboard-status-types';

const PRODUCT_SCAN_SOURCE_VALUES = new Set<string>(
  Object.values(ProductScanSource),
);
const PROFILE_VIEW_SOURCE_VALUES = new Set<string>(
  Object.values(ProfileViewSource),
);

function isProductScanSource(value: string): value is ProductScanSource {
  return PRODUCT_SCAN_SOURCE_VALUES.has(value);
}

function isProfileViewSource(value: string): value is ProfileViewSource {
  return PROFILE_VIEW_SOURCE_VALUES.has(value);
}

function mapProfileViews(
  profileViews: StatusBoardProfileViewsDto | null | undefined,
): DashboardProfileViewMetrics {
  if (!profileViews) return EMPTY_PROFILE_VIEW_METRICS;
  return {
    totalViews: profileViews.totalViews,
    previousPeriodViews: profileViews.previousPeriodViews,
    periodChange: mapPeriodChange(profileViews.periodChange),
    sources: profileViews.sources
      .filter((row) => isProfileViewSource(row.source))
      .map((row) => ({
        source: row.source,
        count: row.count,
      })),
    viewSeries: profileViews.viewSeries,
    previousViewSeries: profileViews.previousViewSeries,
    viaProductCount: profileViews.viaProductCount,
  };
}

function mapPeriodChange(
  change: StatusBoardProductsDto['periodChange'] | undefined,
): DashboardPeriodChange {
  if (!change) {
    return { percent: 0, direction: 'flat' };
  }
  const direction =
    change.direction === StatusBoardPeriodChangeDirection.UP
      ? 'up'
      : change.direction === StatusBoardPeriodChangeDirection.DOWN
        ? 'down'
        : change.direction === StatusBoardPeriodChangeDirection.NEW
          ? 'new'
          : 'flat';
  return { percent: change.percent, direction };
}

function mapProducts(
  products: StatusBoardProductsDto | null | undefined,
  rangeDays: DashboardRangeDays,
): DashboardProductMetrics {
  if (!products) {
    return {
      ...EMPTY_PRODUCT_METRICS,
      scanSeries: buildScanSeries([], rangeDays),
      previousScanSeries: buildScanSeries([], rangeDays),
    };
  }
  return {
    totalProducts: products.totalProducts,
    totalScans: products.totalScans,
    previousPeriodScans: products.previousPeriodScans,
    periodChange: mapPeriodChange(products.periodChange),
    activeProductsCount: products.activeProductsCount,
    scanSeries: products.scanSeries.map((p) => ({
      date: p.date,
      scans: p.scans,
    })),
    previousScanSeries: products.previousScanSeries.map((p) => ({
      date: p.date,
      scans: p.scans,
    })),
    scanSources: (products.scanSources ?? [])
      .filter((row) => isProductScanSource(row.source))
      .map((row) => ({
        source: row.source,
        count: row.count,
      })),
    linkTargets: decorateStatusBoardLinkTargets(products.linkTargets),
    topBusinessCards: decorateStatusBoardTopCards(
      products.topBusinessCards.map((row) => ({
        userId: row.userId,
        name: row.name,
        initials: row.initials,
        avatarUrl: row.avatarPath ? getAbsoluteFileUrl(row.avatarPath) : null,
        meta: row.meta,
        taps: row.taps,
      })),
    ),
  };
}

export function useDashboardStatus(
  labels: DashboardStatusLabels,
  insightTemplates: Record<string, string> = {},
  rangeDays: DashboardRangeDays = 30,
) {
  const workspace = useCurrentWorkspace();
  const workspaceId = workspace?.id;
  const queryClient = useQueryClient();
  useInvalidateOnIonViewEnter(DASHBOARD_REFRESH_QUERY_KEYS);

  const {
    data: board,
    isPending: boardPending,
    isError: boardIsError,
    error: boardQueryError,
  } = useQuery({
    queryKey: workspaceId
      ? statusBoardQueryKeys.board(workspaceId, rangeDays)
      : statusBoardQueryKeys.root,
    queryFn: async (): Promise<StatusBoardDto> => {
      if (!workspaceId) {
        throw new Error('workspaceId required');
      }
      const res = await StatusBoardApi.getBoard({
        path: { workspaceId },
        query: { rangeDays },
      });
      return res.data;
    },
    enabled: Boolean(workspaceId),
    staleTime: 30_000,
    retry: (failureCount, error) => !isAuthzApiError(error) && failureCount < 2,
  });

  const caps = board?.capabilities;
  const reputationEnabled = Boolean(caps?.canReadReputation);
  const feedbackEnabled = Boolean(caps?.canReadFeedback);
  const canManageLocations = Boolean(caps?.canManageLocations);
  const canReadLocations = Boolean(caps?.canReadLocations);
  const canReadProducts = Boolean(caps?.canReadProducts);
  const canReadLeads = Boolean(caps?.canReadLeads);
  const canManageProducts = Boolean(caps?.canManageProducts);
  const canConfigureReputation = Boolean(caps?.canConfigureReputation);

  const locations = useMemo(
    () =>
      (board?.locations ?? []).map((loc) => ({
        id: loc.id,
        name: loc.name ?? undefined,
        address: loc.address ?? undefined,
      })),
    [board?.locations],
  );

  const overview = board?.reputation ?? undefined;
  const summary = board?.reputationSummary ?? undefined;
  const channelLocations = useMemo(
    () => board?.channelLocations ?? [],
    [board?.channelLocations],
  );
  const feedbackStats = board?.feedback as FeedbackStatsDto | undefined;
  const productMetrics = useMemo(
    () => mapProducts(board?.products, rangeDays),
    [board?.products, rangeDays],
  );
  const profileViewMetrics = useMemo(
    () => mapProfileViews(board?.profileViews),
    [board?.profileViews],
  );
  const leadsCreatedSeries = useMemo(
    () =>
      (board?.leads?.createdSeries ?? []).map((p) => ({
        date: p.date,
        value: p.value,
      })),
    [board?.leads?.createdSeries],
  );
  const reviewVolumeSeries = useMemo(
    () =>
      (board?.reviewVolumeSeries ?? []).map((p) => ({
        date: p.date,
        value: p.value,
      })),
    [board?.reviewVolumeSeries],
  );
  const feedbackEntrySeries = useMemo(
    () =>
      (board?.feedbackEntrySeries ?? []).map((p) => ({
        date: p.date,
        value: p.value,
      })),
    [board?.feedbackEntrySeries],
  );
  const newLeadsCount = board?.leads?.newCount ?? 0;
  const profileIsNull = board?.profile ? !board.profile.hasProfile : false;
  const profileLoading = boardPending && Boolean(caps?.canReadProfiles);

  const setup = useReputationConnectionSetupState();

  const { data: notifications = [] } = useNotificationInbox({
    unreadOnly: true,
  });
  const { data: unreadCount = 0 } = useUnreadNotificationCount();

  const [liveScans, setLiveScans] = useState<DashboardLiveScan[]>([]);
  const [highlightedScanId, setHighlightedScanId] = useState<string | null>(
    null,
  );
  const seenScanKeysRef = useRef(new Set<string>());
  const highlightTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  const onRealtimeEvent = useCallback(
    (event: RealtimeEvent) => {
      if (!workspaceId || event.workspaceId !== workspaceId) return;

      const scan = toProductScan(event);
      if (scan) {
        const dedupeKey = `${scan.productId}:${scan.scannedAt}`;
        if (seenScanKeysRef.current.has(dedupeKey)) return;
        seenScanKeysRef.current.add(dedupeKey);
        if (seenScanKeysRef.current.size > 100) {
          const oldest = seenScanKeysRef.current.values().next().value;
          if (oldest) seenScanKeysRef.current.delete(oldest);
        }

        const name = scan.label?.trim() || scan.hash || scan.productId;
        setLiveScans((prev) =>
          [
            {
              id: dedupeKey,
              productId: scan.productId,
              name,
              scannedAt: scan.scannedAt,
            },
            ...prev,
          ].slice(0, LIVE_SCAN_LIMIT),
        );
        setHighlightedScanId(dedupeKey);
        if (highlightTimeoutRef.current) {
          clearTimeout(highlightTimeoutRef.current);
        }
        highlightTimeoutRef.current = setTimeout(() => {
          setHighlightedScanId(null);
        }, 5000);

        const today = new Date().toISOString().slice(0, 10);
        queryClient.setQueryData<StatusBoardDto>(
          statusBoardQueryKeys.board(workspaceId, rangeDays),
          (prev) => {
            if (!prev?.products) return prev;
            const scanSeries = prev.products.scanSeries.map((point) =>
              point.date === today
                ? { ...point, scans: point.scans + 1 }
                : point,
            );
            const totalScans = prev.products.totalScans + 1;
            const periodChange = periodOverPeriodChange(
              totalScans,
              prev.products.previousPeriodScans,
            );
            return {
              ...prev,
              products: {
                ...prev.products,
                totalScans,
                scanSeries,
                periodChange: {
                  percent: periodChange.percent,
                  direction:
                    periodChange.direction === 'up'
                      ? StatusBoardPeriodChangeDirection.UP
                      : periodChange.direction === 'down'
                        ? StatusBoardPeriodChangeDirection.DOWN
                        : periodChange.direction === 'new'
                          ? StatusBoardPeriodChangeDirection.NEW
                          : StatusBoardPeriodChangeDirection.FLAT,
                },
              },
            };
          },
        );
        return;
      }

      if (
        event.type === 'review_sync.started' ||
        event.type === 'review_sync.completed' ||
        event.type === 'review_sync.failed' ||
        event.type === 'review.created' ||
        event.type === 'review.updated'
      ) {
        void queryClient.invalidateQueries({
          queryKey: statusBoardQueryKeys.root,
        });
      }
    },
    [queryClient, rangeDays, workspaceId],
  );

  const { connectionState } = useWorkspaceRealtime({
    workspaceId,
    enabled: Boolean(workspaceId) && canReadProducts,
    onEvent: onRealtimeEvent,
  });

  useEffect(() => {
    return () => {
      if (highlightTimeoutRef.current) {
        clearTimeout(highlightTimeoutRef.current);
      }
    };
  }, []);

  const failedSyncLocationIds = useMemo(
    () => locationIdsWithFailedSync(channelLocations),
    [channelLocations],
  );

  const healthRows = useMemo(
    () =>
      buildDashboardHealthRows({
        reputationEnabled,
        overview,
        locations,
        failedSyncLocationIds,
        channelLocations,
        labels,
      }),
    [
      channelLocations,
      failedSyncLocationIds,
      labels,
      locations,
      overview,
      reputationEnabled,
    ],
  );

  const doNextItems = useMemo(
    () =>
      buildDashboardDoNextItems({
        reputationEnabled,
        overview,
        failedSyncLocationIds,
        canConfigureReputation,
        showConnectionBanner: setup.showConnectionBanner,
        bannerVariant: setup.bannerVariant,
        needsActionCount: setup.needsActionCount,
        newLeadsCount,
        feedbackEnabled,
        unrepliedFeedbackCount: feedbackStats?.unrepliedCount ?? 0,
        canManageLocations,
        locationsPending: boardPending && canReadLocations,
        locationsCount: locations.length,
        profileLoading,
        profileIsNull: Boolean(caps?.canReadProfiles) && profileIsNull,
        canManageProducts,
        productsPending: boardPending && canReadProducts,
        totalProducts: productMetrics.totalProducts,
        productsError: Boolean(boardIsError && canReadProducts),
        summary,
        insightTemplates,
        labels,
      }),
    [
      boardIsError,
      boardPending,
      canConfigureReputation,
      canManageLocations,
      canManageProducts,
      canReadLocations,
      canReadProducts,
      caps?.canReadProfiles,
      failedSyncLocationIds,
      feedbackEnabled,
      feedbackStats?.unrepliedCount,
      insightTemplates,
      labels,
      locations.length,
      newLeadsCount,
      overview,
      productMetrics.totalProducts,
      profileIsNull,
      profileLoading,
      reputationEnabled,
      setup.bannerVariant,
      setup.needsActionCount,
      setup.showConnectionBanner,
      summary,
    ],
  );

  const healthMix = useMemo((): DashboardHealthMix => {
    const mix: DashboardHealthMix = {
      critical: 0,
      attention: 0,
      ok: 0,
      unknown: 0,
      total: 0,
    };
    for (const row of healthRows) {
      mix[healthStatusBucket(row.status)] += 1;
      mix.total += 1;
    }
    return mix;
  }, [healthRows]);

  const attentionReviews = useMemo(
    () =>
      (overview?.recentCritical ?? [])
        .filter((review) => !review.responded)
        .slice(0, ATTENTION_REVIEW_LIMIT),
    [overview?.recentCritical],
  );

  const attentionNotifications = useMemo(
    () => notifications.slice(0, ATTENTION_NOTIFICATION_LIMIT),
    [notifications],
  );

  const attentionBreakdown = useMemo(() => {
    const fromCount = overview?.unrepliedCriticalCount ?? 0;
    const fromRecent = attentionReviews.length;
    const criticalReviews =
      reputationEnabled && Math.max(fromCount, fromRecent) > 0
        ? Math.max(fromCount, fromRecent)
        : 0;
    const setupGaps = setup.showConnectionBanner ? 1 : 0;
    const newLeads = newLeadsCount;
    const syncFailed = failedSyncLocationIds.size;
    const zeroReviews = reputationEnabled
      ? (overview?.locationsWithoutReviews?.length ?? 0)
      : 0;
    return {
      criticalReviews,
      setupGaps,
      newLeads,
      syncFailed,
      zeroReviews,
      total: criticalReviews + setupGaps + newLeads + syncFailed + zeroReviews,
    };
  }, [
    attentionReviews.length,
    failedSyncLocationIds.size,
    newLeadsCount,
    overview?.locationsWithoutReviews?.length,
    overview?.unrepliedCriticalCount,
    reputationEnabled,
    setup.showConnectionBanner,
  ]);

  const attentionCount = attentionBreakdown.total;

  const isLoading = boardPending || (reputationEnabled && !setup.isReady);

  const refetchAll = useCallback(() => {
    void invalidateDashboardStatusQueries(queryClient);
  }, [queryClient]);

  const boardError = boardIsError && !isAuthzApiError(boardQueryError);

  return {
    workspaceId,
    reputationEnabled,
    canManageLocations,
    canReadLocations,
    canReadProducts,
    feedbackEnabled,
    feedbackStats,
    isLoading,
    locationsError: boardError && canReadLocations,
    overviewError: boardError && reputationEnabled,
    leadsError: boardError && canReadLeads,
    productsError: boardError && canReadProducts,
    refetchAll,
    healthRows,
    healthMix,
    doNextItems,
    attentionReviews,
    attentionNotifications,
    unreadCount,
    attentionCount,
    attentionBreakdown,
    overview,
    locations,
    productMetrics,
    profileViewMetrics,
    canReadProfiles: Boolean(caps?.canReadProfiles),
    canManageProducts,
    canReadLeads,
    newLeadsCount,
    leadsCreatedSeries,
    reviewVolumeSeries,
    feedbackEntrySeries,
    liveScans,
    highlightedScanId,
    connectionState: connectionState as WorkspaceRealtimeConnectionState,
    setup,
  };
}
